import React, { useContext } from 'react';

import { SyncReviewIntegrationNames } from 'common/constants/autopilotIntegrations';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';

import { DuplicatePostSuggestion, UniquePostSuggestion } from './Suggestion';

import type { Company } from 'common/api/endpoints/companies';
import type { ActionItem, PostActionItem, PostDraftActionItem } from 'common/api/endpoints/queue';

type Props = {
  item: ActionItem;
  company: Company;
};

const PostDraftSuggestedAction = ({
  company,
  item,
}: {
  company: Company;
  item: PostDraftActionItem;
}) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);

  const { sourcePostDraft } = item;

  const isReviewSource = Object.values(SyncReviewIntegrationNames).includes(
    item.source as unknown as SyncReviewIntegrationNames
  );
  const defaultLink = isReviewSource
    ? '/admin/settings/autopilot/feedback-discovery'
    : '/admin/settings/integrations';

  if (!item.duplicatePost) {
    const onClick = () => {
      if (item.createdPost) {
        return router.replace({
          pathname: location.pathname,
          query: { ...location.query, postURLName: item.createdPost.urlName },
        });
      }

      window.open(item.link ?? defaultLink, '_blank', 'noopener,noreferrer');
    };

    return (
      <UniquePostSuggestion
        title={sourcePostDraft.title}
        translation={sourcePostDraft.translation}
        details={sourcePostDraft.details}
        board={item.board}
        author={sourcePostDraft.author}
        created={sourcePostDraft.createdAt}
        workaround={sourcePostDraft.workaround}
        onClick={onClick}
      />
    );
  }

  return (
    <DuplicatePostSuggestion
      sourceType="draft"
      title={sourcePostDraft.title}
      translation={sourcePostDraft.translation}
      details={sourcePostDraft.details}
      author={sourcePostDraft.author}
      created={sourcePostDraft.createdAt}
      board={item.duplicatePost.board}
      duplicatePost={item.duplicatePost}
      company={company}
      workaround={sourcePostDraft.workaround}
      onClickDuplicatePost={() =>
        window.open(item.link ?? defaultLink, '_blank', 'noopener,noreferrer')
      }
      onClickOriginalPost={() =>
        router.replace({
          pathname: location.pathname,
          query: { ...location.query, postURLName: item.duplicatePost?.urlName },
        })
      }
    />
  );
};

const CannyPostSuggestedAction = ({
  company,
  item,
}: {
  company: Company;
  item: PostActionItem;
}) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);

  const { sourcePost, duplicatePost } = item;

  return (
    <DuplicatePostSuggestion
      sourceType="post"
      title={sourcePost.title}
      translation={sourcePost.translation}
      details={sourcePost.details}
      author={sourcePost.author}
      created={sourcePost.created}
      board={duplicatePost.board}
      duplicatePost={duplicatePost}
      company={company}
      onClickDuplicatePost={() =>
        router.replace({
          pathname: location.pathname,
          query: { ...location.query, postURLName: item.duplicatePost.urlName },
        })
      }
      onClickOriginalPost={() =>
        router.replace({
          pathname: location.pathname,
          query: { ...location.query, postURLName: item.duplicatePost.urlName },
        })
      }
    />
  );
};

const AuditLogSuggestionCell = ({ company, item }: Props) => {
  if (item.sourceType === 'draft') {
    return <PostDraftSuggestedAction company={company} item={item} />;
  }

  if (item.sourceType === 'post') {
    return <CannyPostSuggestedAction company={company} item={item} />;
  }

  return null;
};

export default AuditLogSuggestionCell;
